import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, inr, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Card, CardHead, Chips, Empty, Field, Input, Modal, Notice, SearchInput, Textarea, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';

type GapType = 'CUSTOMER' | 'EMPLOYEE';

interface Programme {
  type: GapType;
  activeLinks: number;
  linkOpens: number;
  members: number;
  suspendedMembers: number;
  pendingRegistrations: number;
  payments: { count: number; amountPaise: number };
  commissions: Record<string, { count: number; amountPaise: number }>;
}

interface MemberRow {
  id: string;
  memberId: string;
  type: GapType;
  status: 'ACTIVE' | 'SUSPENDED';
  suspendedReason: string | null;
  joinedAt: string | null;
  name: string | null;
  mobile: string | null;
  partnerId: string | null;
  sponsorId: string | null;
  joinedThroughOwnerLink: boolean;
  directReferrals: number;
  payment: { transactionId: string | null; amountPaise: number; status: string; paidAt: string | null } | null;
}

interface RegistrationRow {
  id: string;
  status: 'CREATED' | 'AUTHORIZED' | 'CAPTURED' | 'FAILED' | 'REFUNDED';
  amountPaise: number;
  orderId: string;
  transactionId: string | null;
  method: string | null;
  failureReason: string | null;
  createdAt: string;
  capturedAt: string | null;
  refundedAt: string | null;
  type: GapType;
  sponsorId: string | null;
  name: string | null;
  mobile: string | null;
  partnerId: string | null;
  memberId: string | null;
  duplicate: boolean;
}

interface LinkRow {
  id: string;
  token: string;
  url: string;
  type: GapType;
  sponsorId: string;
  sponsorName: string | null;
  isOwnerLink: boolean;
  isActive: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  opens: number;
  lastOpenedAt: string | null;
  registrations: number;
  createdAt: string;
}

const typeLabel = (t: GapType) => (t === 'CUSTOMER' ? 'Global Customer' : 'Global Employee');

const PAYMENT: Record<RegistrationRow['status'], { label: string; tone: BadgeTone }> = {
  CREATED: { label: 'Pending', tone: 'amber' },
  AUTHORIZED: { label: 'Pending', tone: 'amber' },
  CAPTURED: { label: 'Successful', tone: 'green' },
  FAILED: { label: 'Failed', tone: 'red' },
  REFUNDED: { label: 'Refunded', tone: 'neutral' },
};

/* ------------------------------------------------------------- dashboard */

/** The owner's dashboard, as the console sees it: both programmes side by side. */
export function GapDashboardView() {
  const q = useQuery({
    queryKey: ['gap-dashboard'],
    queryFn: () => api.get<{ customer: Programme; employee: Programme }>('/admin/gap/dashboard'),
  });
  if (q.isLoading) return <Card>Loading…</Card>;
  if (!q.data) return <Notice tone="warn" title="Could not load the dashboard">{(q.error as Error)?.message}</Notice>;
  return (
    <div className="grid grid--2">
      {[q.data.customer, q.data.employee].map((p) => (
        <Card key={p.type}>
          <CardHead title={typeLabel(p.type)} sub={`${p.activeLinks} active owner links · opened ${p.linkOpens} times`} />
          <div className="grid grid--stats" style={{ marginTop: 'var(--s4)' }}>
            <Figure label="Members" value={String(p.members)} sub={p.suspendedMembers ? `${p.suspendedMembers} suspended` : undefined} />
            <Figure label="Pending registrations" value={String(p.pendingRegistrations)} />
            <Figure label="Successful payments" value={inr(p.payments.amountPaise)} sub={`${p.payments.count} payments`} />
            <Figure
              label="Commission owed"
              value={inr((p.commissions.PENDING?.amountPaise ?? 0) + (p.commissions.APPROVED?.amountPaise ?? 0))}
              sub={`${inr(p.commissions.PAID?.amountPaise ?? 0)} paid · ${inr(p.commissions.ON_HOLD?.amountPaise ?? 0)} held`}
            />
          </div>
        </Card>
      ))}
    </div>
  );
}

function Figure({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      {sub && <div className="stat__delta stat__delta--flat">{sub}</div>}
    </div>
  );
}

/* --------------------------------------------------------------- members */

export function GapMembersTable({ type }: { type: GapType }) {
  const { get, set } = useListParams();
  const status = get('status');
  const search = get('search');
  const [suspending, setSuspending] = useState<MemberRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/admin/gap/ids/${id}/activate`),
    onSuccess: () => {
      toast('Activated', 'success');
      qc.invalidateQueries({ queryKey: ['gap-members'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<MemberRow>> = [
    {
      key: 'member',
      header: 'Member',
      render: (m) => (
        <div>
          <div className="cell-strong cell-mono">{m.memberId}</div>
          <div className="cell-muted">
            {m.name ?? 'Unnamed'}
            {m.mobile ? ` · ${fmtMobile(m.mobile)}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'sponsor',
      header: 'Sponsor ID',
      render: (m) => (
        <div>
          <span className="cell-mono">{m.sponsorId ?? '—'}</span>
          {m.joinedThroughOwnerLink && <div className="cell-muted">Owner's link</div>}
        </div>
      ),
    },
    { key: 'refs', header: 'Direct referrals', numeric: true, render: (m) => m.directReferrals },
    {
      key: 'payment',
      header: 'Payment',
      render: (m) =>
        m.payment ? (
          <div>
            <div>{inr(m.payment.amountPaise)}</div>
            <div className="cell-muted cell-mono">{m.payment.transactionId ?? '—'}</div>
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    { key: 'joined', header: 'Joined', render: (m) => <span className="cell-muted">{m.joinedAt ? dateTime(m.joinedAt) : '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (m) => (
        <div>
          <Badge tone={m.status === 'ACTIVE' ? 'green' : 'red'} dot>
            {m.status === 'ACTIVE' ? 'Active' : 'Suspended'}
          </Badge>
          {m.suspendedReason && <div className="cell-muted">{m.suspendedReason}</div>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (m) => (
        <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
          <Link to={`/gap-ids?view=trace&q=${m.memberId}`}>Tree</Link>
          {m.status === 'ACTIVE' ? (
            <Button size="sm" variant="danger" onClick={() => setSuspending(m)}>
              Suspend
            </Button>
          ) : (
            <Button size="sm" variant="success" loading={activate.isPending && activate.variables === m.id} onClick={() => activate.mutate(m.id)}>
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable<MemberRow>
        queryKey={['gap-members', type, status, search]}
        path="/admin/gap/members"
        params={{ type, status, search }}
        columns={columns}
        emptyIcon="👥"
        emptyTitle={`No ${typeLabel(type)} members yet`}
        emptyBody="A member appears here once their ₹2,000 membership payment is verified."
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <Chips
              value={status}
              onChange={(v) => set('status', v)}
              options={[
                { value: '', label: 'All' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'SUSPENDED', label: 'Suspended' },
              ]}
            />
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Partner ID, name or mobile…" />
          </div>
        }
      />
      {suspending && (
        <ReasonModal
          title={`Suspend ${suspending.memberId}`}
          sub="Their link stops inviting anybody and they earn nothing until activated again."
          action="Suspend"
          path={`/admin/gap/ids/${suspending.id}/suspend`}
          onClose={() => setSuspending(null)}
          onDone={() => {
            toast('Suspended', 'success');
            setSuspending(null);
            qc.invalidateQueries({ queryKey: ['gap-members'] });
          }}
        />
      )}
    </>
  );
}

/* --------------------------------------------------------- registrations */

/** Membership payments: pending ones, successful ones (with a refund), failed and refunded. */
export function GapRegistrationsTable({ initialStatus }: { initialStatus: string }) {
  const { get, set } = useListParams();
  const status = get('status') || initialStatus;
  const type = get('type');
  const [refunding, setRefunding] = useState<RegistrationRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const columns: Array<Column<RegistrationRow>> = [
    {
      key: 'who',
      header: 'Registrant',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.name ?? 'Unnamed'}</div>
          <div className="cell-muted">{r.mobile ? fmtMobile(r.mobile) : ''}</div>
        </div>
      ),
    },
    { key: 'type', header: 'Programme', render: (r) => typeLabel(r.type) },
    { key: 'sponsor', header: 'Sponsor ID', render: (r) => <span className="cell-mono">{r.sponsorId ?? '—'}</span> },
    { key: 'member', header: 'Partner ID', render: (r) => <span className="cell-mono">{r.memberId ?? '—'}</span> },
    { key: 'amount', header: 'Amount', numeric: true, render: (r) => inr(r.amountPaise) },
    {
      key: 'txn',
      header: 'Transaction',
      render: (r) => (
        <div>
          <div className="cell-mono">{r.transactionId ?? r.orderId}</div>
          <div className="cell-muted">{dateTime(r.capturedAt ?? r.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div>
          <Badge tone={PAYMENT[r.status].tone} dot>
            {PAYMENT[r.status].label}
          </Badge>
          {r.duplicate && <div className="cell-muted" style={{ color: 'var(--danger)' }}>Duplicate: refund it</div>}
          {r.failureReason && <div className="cell-muted">{r.failureReason}</div>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'CAPTURED' ? (
          <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="danger" onClick={() => setRefunding(r)}>
              Refund
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable<RegistrationRow>
        queryKey={['gap-registrations', status, type]}
        path="/admin/gap/registrations"
        params={{ status, type }}
        columns={columns}
        emptyIcon="🧾"
        emptyTitle="Nothing here"
        emptyBody="Registrations appear once somebody invited through a GAP link opens the ₹2,000 payment."
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <Chips
              value={status}
              onChange={(v) => set('status', v)}
              options={[
                { value: 'CREATED', label: 'Pending' },
                { value: 'CAPTURED', label: 'Successful' },
                { value: 'FAILED', label: 'Failed' },
                { value: 'REFUNDED', label: 'Refunded' },
              ]}
            />
            <Chips
              value={type}
              onChange={(v) => set('type', v)}
              options={[
                { value: '', label: 'Both' },
                { value: 'CUSTOMER', label: 'Customer' },
                { value: 'EMPLOYEE', label: 'Employee' },
              ]}
            />
          </div>
        }
      />
      {refunding && (
        <ReasonModal
          title={`Refund ${inr(refunding.amountPaise)} to ${refunding.name ?? 'this member'}`}
          sub="Records the refund here; raise the money back in the Razorpay dashboard. Every commission this payment produced is reversed, and the member is suspended."
          action="Record refund"
          path={`/admin/payments/${refunding.id}/refund`}
          onClose={() => setRefunding(null)}
          onDone={() => {
            toast('Refund recorded; commissions reversed', 'success');
            setRefunding(null);
            qc.invalidateQueries({ queryKey: ['gap-registrations'] });
            qc.invalidateQueries({ queryKey: ['gap-commissions'] });
          }}
        />
      )}
    </>
  );
}

/* ----------------------------------------------------------------- links */

export function GapLinksTable() {
  const { get, set } = useListParams();
  const owner = get('owner');
  const active = get('active');
  const search = get('search');
  const [revoking, setRevoking] = useState<LinkRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/admin/gap/links/${id}/restore`),
    onSuccess: () => {
      toast('Restored', 'success');
      qc.invalidateQueries({ queryKey: ['gap-links'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<LinkRow>> = [
    {
      key: 'link',
      header: 'Link',
      render: (l) => (
        <div>
          <div className="cell-mono">{l.token}</div>
          <div className="cell-muted">{l.isOwnerLink ? "Owner's link" : "Member's own link"} · {typeLabel(l.type)}</div>
        </div>
      ),
    },
    {
      key: 'sponsor',
      header: 'Sponsor ID',
      render: (l) => (
        <div>
          <div className="cell-mono">{l.sponsorId}</div>
          <div className="cell-muted">{l.sponsorName ?? ''}</div>
        </div>
      ),
    },
    { key: 'opens', header: 'Opened', numeric: true, render: (l) => l.opens },
    { key: 'joined', header: 'Joined', numeric: true, render: (l) => l.registrations },
    { key: 'created', header: 'Created', render: (l) => <span className="cell-muted">{dateTime(l.createdAt)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (l) => (
        <div>
          <Badge tone={l.isActive ? 'green' : 'red'} dot>
            {l.isActive ? 'Active' : 'Revoked'}
          </Badge>
          {l.revokedReason && <div className="cell-muted">{l.revokedReason}</div>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (l) => (
        <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
          {l.isActive ? (
            <Button size="sm" variant="danger" onClick={() => setRevoking(l)}>
              Revoke
            </Button>
          ) : (
            <Button size="sm" loading={restore.isPending && restore.variables === l.id} onClick={() => restore.mutate(l.id)}>
              Restore
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable<LinkRow>
        queryKey={['gap-links', owner, active, search]}
        path="/admin/gap/links"
        params={{ owner, active, search }}
        columns={columns}
        emptyIcon="🔗"
        emptyTitle="No links yet"
        emptyBody="The owner generates links from the app's GAP dashboard; each member's own link is made when they first open theirs."
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <Chips
              value={owner}
              onChange={(v) => set('owner', v)}
              options={[
                { value: '', label: 'All' },
                { value: 'true', label: "Owner's" },
                { value: 'false', label: "Members'" },
              ]}
            />
            <Chips
              value={active}
              onChange={(v) => set('active', v)}
              options={[
                { value: '', label: 'Any' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Revoked' },
              ]}
            />
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Token or Sponsor ID…" />
          </div>
        }
      />
      {revoking && (
        <ReasonModal
          title={`Revoke ${revoking.token}`}
          sub="It stops inviting anybody. People who already joined through it stay members."
          action="Revoke"
          optional
          path={`/admin/gap/links/${revoking.id}/revoke`}
          onClose={() => setRevoking(null)}
          onDone={() => {
            toast('Revoked', 'success');
            setRevoking(null);
            qc.invalidateQueries({ queryKey: ['gap-links'] });
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------- tree and trace */

interface Trace {
  member: {
    id: string;
    code: string;
    type: GapType;
    isActive: boolean;
    holder: { id: string; displayName: string | null; mobile: string } | null;
    directReferrals: Array<{ name: string | null; partnerId: string | null; customerId: string | null; joinedAt: string }>;
    chain: Array<{ code: string; name: string | null; level: number; joinedAt: string; rewardEarned: boolean; referredBy: string }>;
    commission: { totalEarnedPaise: number; paidPaise: number };
  };
  sponsors: Array<{ memberId: string; type: GapType; name: string | null; isOwner: boolean }>;
  joinedThrough: { token: string; isOwnerLink: boolean; createdAt: string } | null;
}

/** Sponsor ID tracking and the referral tree, for one member. */
export function GapTraceView() {
  const { get, set } = useListParams();
  const q = get('q');
  const [text, setText] = useState(q);
  const trace = useQuery({
    queryKey: ['gap-trace', q],
    queryFn: () => api.get<Trace>(`/admin/gap/trace?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 3,
    retry: false,
  });
  const t = trace.data;
  return (
    <div className="stack">
      <Card>
        <form
          className="row row--wrap"
          style={{ gap: 'var(--s4)' }}
          onSubmit={(e) => {
            e.preventDefault();
            set('q', text.trim());
          }}
        >
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Partner ID or mobile" style={{ maxWidth: 320 }} />
          <Button type="submit" variant="primary" disabled={text.trim().length < 3}>
            Trace
          </Button>
        </form>
      </Card>
      {trace.isError && <Notice tone="warn" title="Not found">{(trace.error as Error).message}</Notice>}
      {t && (
        <>
          <Card>
            <CardHead
              title={`${t.member.code} · ${t.member.holder?.displayName ?? 'Unnamed'}`}
              sub={`${typeLabel(t.member.type)} · ${t.member.isActive ? 'Active' : 'Suspended'} · earned ${inr(t.member.commission.totalEarnedPaise)}, paid ${inr(t.member.commission.paidPaise)}`}
            />
            <div className="cell-muted" style={{ marginTop: 'var(--s3)' }}>
              {t.joinedThrough
                ? `Joined through ${t.joinedThrough.isOwnerLink ? "the owner's" : "a member's"} link ${t.joinedThrough.token}`
                : 'No link on record (joined before links, or assigned by hand).'}
            </div>
          </Card>
          <Card>
            <CardHead title="Sponsors, up to the owner" />
            {t.sponsors.length === 0 ? (
              <Empty title="No sponsor on record" />
            ) : (
              <ol style={{ margin: 'var(--s3) 0 0', paddingLeft: 20 }}>
                {t.sponsors.map((s) => (
                  <li key={s.memberId} style={{ marginBottom: 6 }}>
                    <Link to={`/gap-ids?view=trace&q=${s.memberId}`} onClick={() => setText(s.memberId)} className="cell-mono">
                      {s.memberId}
                    </Link>{' '}
                    {s.name ?? ''} {s.isOwner && <Badge tone="blue">Owner</Badge>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
          <Card>
            <CardHead title={`Referral tree below (${t.member.chain.length})`} sub="Level 1 joined through this member; level 2 through theirs, and on." />
            {t.member.chain.length === 0 ? (
              <Empty title="Nobody has joined through this member yet" />
            ) : (
              <div style={{ marginTop: 'var(--s3)' }}>
                {t.member.chain.map((n) => (
                  <div key={n.code} style={{ paddingLeft: (n.level - 1) * 22, marginBottom: 6 }}>
                    <Badge tone="neutral">L{n.level}</Badge>{' '}
                    <Link to={`/gap-ids?view=trace&q=${n.code}`} onClick={() => setText(n.code)} className="cell-mono">
                      {n.code}
                    </Link>{' '}
                    {n.name ?? ''} <span className="cell-muted">· via {n.referredBy} · {dateTime(n.joinedAt)}</span>
                    {n.rewardEarned && <> <Badge tone="green">earned</Badge></>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- shared */

/** A confirmation that asks for a reason and posts it. */
interface EmployeeApplicationRow {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  displayName: string;
  email: string | null;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  submittedAt: string;
  decidedAt: string | null;
  reason: string | null;
  mobile: string;
  sponsorId: string;
  employeeId: string | null;
  partnerId: string | null;
}

const APPLICATION: Record<EmployeeApplicationRow['status'], { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'Waiting for approval', tone: 'amber' },
  APPROVED: { label: 'Approved', tone: 'green' },
  REJECTED: { label: 'Not approved', tone: 'red' },
};

/**
 * Registrations from the owner's Employee links. Nobody pays: an employee is
 * a partner only once approved here, with their Partner ID and the membership
 * marked waived as a company employee.
 */
export function GapEmployeeApplicationsTable() {
  const { get, set } = useListParams();
  const status = get('status') || 'PENDING';
  const search = get('search');
  const [rejecting, setRejecting] = useState<EmployeeApplicationRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/admin/gap/employee-applications/${id}/approve`),
    onSuccess: () => {
      toast('Approved: their partner dashboard is open', 'success');
      qc.invalidateQueries({ queryKey: ['gap-employee-applications'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<EmployeeApplicationRow>> = [
    {
      key: 'who',
      header: 'Employee',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.displayName}</div>
          <div className="cell-muted">{fmtMobile(r.mobile)}</div>
          {r.email && <div className="cell-muted">{r.email}</div>}
        </div>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (r) => (
        <div className="cell-muted" style={{ maxWidth: 260 }}>
          <div>Born {new Date(r.dateOfBirth).toLocaleDateString('en-IN')}</div>
          <div>
            {r.address}, {r.city}, {r.state} {r.pincode}
          </div>
        </div>
      ),
    },
    { key: 'sponsor', header: 'Sponsor ID', render: (r) => <span className="cell-mono">{r.sponsorId}</span> },
    {
      key: 'ids',
      header: 'Partner ID',
      render: (r) =>
        r.employeeId ? (
          <div>
            <div className="cell-mono">{r.employeeId}</div>
            {r.partnerId && r.partnerId !== r.employeeId && <div className="cell-muted">{r.partnerId}</div>}
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div>
          <Badge tone={APPLICATION[r.status].tone} dot>
            {APPLICATION[r.status].label}
          </Badge>
          <div className="cell-muted">{dateTime(r.decidedAt ?? r.submittedAt)}</div>
          {r.reason && <div className="cell-muted">{r.reason}</div>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'PENDING' ? (
          <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="success" loading={approve.isPending && approve.variables === r.id} onClick={() => approve.mutate(r.id)}>
              Approve
            </Button>
            <Button size="sm" variant="danger" onClick={() => setRejecting(r)}>
              Reject
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <Notice tone="info" title="Employee links: no payment">
        People who open the owner&apos;s Employee link register without paying and become partners at once, with their own
        Partner ID: there is nothing to approve. Their link then brings in members who pay ₹2,000, earning them ₹100 each.
        Approve is only for a registration left waiting from before.
      </Notice>
      <DataTable<EmployeeApplicationRow>
        queryKey={['gap-employee-applications', status, search]}
        path="/admin/gap/employee-applications"
        params={{ status, search }}
        columns={columns}
        emptyIcon="🧑‍💼"
        emptyTitle={status === 'PENDING' ? 'Nobody waiting' : 'Nothing here'}
        emptyBody="Registrations from the owner's Employee links appear here."
        toolbar={
          <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
            <Chips
              value={status}
              onChange={(v) => set('status', v)}
              options={[
                { value: 'PENDING', label: 'Waiting' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'REJECTED', label: 'Not approved' },
              ]}
            />
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name or mobile" />
          </div>
        }
      />
      {rejecting && (
        <ReasonModal
          title={`Reject ${rejecting.displayName}`}
          sub="They are told this reason in the app, and can register again."
          action="Reject"
          path={`/admin/gap/employee-applications/${rejecting.id}/reject`}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            toast('Registration turned down', 'success');
            qc.invalidateQueries({ queryKey: ['gap-employee-applications'] });
          }}
        />
      )}
    </>
  );
}

export function ReasonModal({
  title,
  sub,
  action,
  path,
  optional,
  onClose,
  onDone,
}: {
  title: string;
  sub: string;
  action: string;
  path: string;
  optional?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const toast = useToast();
  const send = useMutation({
    mutationFn: () => api.post(path, reason.trim() ? { reason: reason.trim() } : {}),
    onSuccess: onDone,
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title={title}
      sub={sub}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!optional && reason.trim().length < 3} loading={send.isPending} onClick={() => send.mutate()}>
            {action}
          </Button>
        </>
      }
    >
      <Field label={optional ? 'Reason (optional)' : 'Reason'} hint="Kept on the record and in the audit log.">
        <Textarea rows={3} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}
